import React, {useRef, useState} from "react";
import {OnboardingSteps} from "@/Pages/Organization/Onboarding/Partials/Steps";
import {XCircle} from "react-feather";
import {FormError, Modal, Subheader} from "@/Components";
import {LabeledInput, LabeledSelect} from "@/Components/Form";
import {Inertia} from "@inertiajs/inertia";
import SwedishOrgNr from "@/Utilities/Utilities";
import {ErrorMessage} from "@/Components/Messages";

export default function CreateYourAccount({
  errors,
  modalVisible,
  setModalVisible,
}) {

  const step_refrences = [
    useRef(),
    useRef(),
    useRef()
  ];

  const defaultValues = {
    organization_number: '',
    name: '',
    user_firstname: '',
    user_surname: '',

    billing_method: 'email',
    billing_email:  '',
    billing_address: '',
    billing_city: '',
    billing_zipcode: '',
    billing_currency: 'sek',

    user_email: '',
    password: '',
    password_confirmation: ''
  };

  const [step, setStep] = useState(0);
  const [values, setValues] = useState(defaultValues);
  const [localErrors, setLocalErrors] = useState({});

  const handleUpdate = (event) => {
    setValues( prevValues => {
      return { ...prevValues,[event.target.name]: event.target.value}
    })
  }

  function validateInputs() {
    let valid = true;

    step_refrences[step].current?.querySelectorAll('input').forEach(input => {

      // Check organization number validity
      if (input.name === 'organization_number') {
        if (!SwedishOrgNr.validate(input.value)) {
          input.setCustomValidity("Please enter a valid organization number with format XXXXXX-XXXX");
        } else {
          input.setCustomValidity("");
        }
      }

      if (input.name === 'password_confirmation') {
        if (input.value !== values.password) {
          input.setCustomValidity("The passwords must match");
        } else {
          input.setCustomValidity("");
        }
      }

      if (!input.checkValidity()) {

        setLocalErrors(prevValues => {
          return {...prevValues, [input.name]: input.validationMessage}
        });

        // First occuring error
        if (valid) {
          input.focus();
        }

        valid = false;
      } else {
        setLocalErrors(prevValues => {
          return {...prevValues, [input.name]: false}
        });
      }
    });
    return valid;
  }

  const handleNextStep = () => {

    if (!validateInputs()) {

      return;
    }

    if (step === 2) {
      Inertia.post('/register-light-account', values, {
        onSuccess: () => {
          onCancel();
        },
        onError: (errors) => {
          let first_error_step = null;

          // Find the first step with an error
          Object.keys(errors).forEach((key) => {
            step_refrences.forEach((stepRef, index) => {

              // Abort if ref is invalid or error already found
              if (first_error_step || !stepRef.current) {
                return;
              }

              if (stepRef.current?.querySelector(`[name="${key}"]`)) {
                first_error_step = index;
              }
            });

            if (first_error_step !== null) {
              setStep(first_error_step);
            }
          });
        }
      });

      return;
    }

    setStep(prevStep => prevStep + 1);

  }

  const onCancel = () => {
    setModalVisible(false);
    setStep(0);
    setValues(defaultValues);
  }


  return <Modal visible={modalVisible} setVisible={setModalVisible}>
    <div className="flex flex-col flex-shrink-0 p-4 md:p-8 bg-white border-t shadow-xl">
      <div className="relative flex flex-row items-center p-4">
        <OnboardingSteps currentStep={step} steps={['Company', 'Billing', 'Account']} padding="" className="-ml-2"/>
        <XCircle className="absolute p-2 cursor-pointer right-2 top-2 md:right-4 md:top-4" size={42} onClick={onCancel} />
      </div>

      <div className="flex flex-col p-4">
        <Subheader>EMMA</Subheader>
        <h4 className="mb-4 text-xl font-bold">Create your account</h4>
        <p>Fill out your information and you will get an email with all information</p>

        {errors?.message &&
          <ErrorMessage className="mt-4">{errors?.message}</ErrorMessage>
        }

        <form className="mt-8">
          <div className={`flex flex-wrap ${step === 0 ? '' : 'hidden'}`} ref={step_refrences[0]}>
            <div className="w-full md:w-1/2 md:pr-3 mb-4">
              <LabeledInput
                  className="bg-[#F6F6F6]"
                title="First name*"
                placeholder="Fill out first name"
                name="user_firstname"
                type="text"
                required="required"
                onChange={handleUpdate}
                value={values.user_firstname}
                error={(localErrors && localErrors.user_firstname) || (errors && errors.user_firstname)}
              />
            </div>

            <div className="w-full md:w-1/2 md:pl-3 mb-4">
              <LabeledInput
                  className="bg-[#F6F6F6]"
                title="Surname*"
                placeholder="Fill out surname"
                name="user_surname"
                type="text"
                required="required"
                onChange={handleUpdate}
                value={values.user_surname}
                error={(localErrors && localErrors.user_surname) || (errors && errors.user_surname)}
              />
            </div>

            <div className="w-full mb-4">
              <LabeledInput
                  className="bg-[#F6F6F6]"
                title="Organization number*"
                placeholder="Enter organization number"
                name="organization_number"
                type="text"
                required="required"
                onChange={handleUpdate}
                value={values.organization_number}
                error={(localErrors && localErrors.organization_number) || (errors && errors.organization_number)}
              />
            </div>

            <div className="w-full mb-4">
              <LabeledInput
                  className="bg-[#F6F6F6]"
                title="Company name*"
                placeholder="Enter company name"
                name="name"
                type="text"
                required="required"
                onChange={handleUpdate}
                value={values.name}
                error={(localErrors && localErrors.name) || (errors && errors.name)}
              />
            </div>
          </div>

          <div className={`flex flex-wrap ${step === 1 ? '' : 'hidden'}`} ref={step_refrences[1]}>
            <div className="w-full mb-4 flex flex-col md:flex-row">
              <label className="mr-4">
                <input
                  checked={values.billing_method === 'email'}
                  value={'email'}
                  type="radio"
                  name="billing_method"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Billing by e-mail</span>
              </label>

              <label className="mr-4">
                <input
                  checked={values.billing_method === 'postal'}
                  value={'postal'}
                  type="radio"
                  name="billing_method"
                  onChange={handleUpdate}
                />
                <span className="ml-2">Billing by snail-mail (0,54 gram CO₂e per bill)</span>
              </label>
              <FormError error={(localErrors && localErrors.billing_method) || (errors && errors.billing_method)}/>
            </div>

            {values.billing_method === 'email' &&
              <>
                <div className="w-full mb-4">
                  <LabeledInput
                      className="bg-[#F6F6F6]"
                    title="E-mail*"
                    placeholder="Enter e-mail"
                    name="billing_email"
                    required="required"
                    type="email"
                    value={values.billing_email}
                    onChange={handleUpdate}
                    error={(localErrors && localErrors.billing_email) || (errors && errors.billing_email)}
                  />
                </div>
              </>
            }

            {values.billing_method === 'postal' &&
              <>
                <div className="w-full mb-4">
                  <LabeledInput
                      className="bg-[#F6F6F6]"
                    title="Billing address*"
                    placeholder="Enter billing address"
                    name="billing_address"
                    type="text"
                    required="required"
                    value={values.billing_address}
                    onChange={handleUpdate}
                    error={(localErrors && localErrors.billing_address) || (errors && errors.billing_address)}
                  />
                </div>

                <div className="w-full mb-4 md:w-1/2">
                  <LabeledInput
                      className="bg-[#F6F6F6]"
                    title="ZIP-code*"
                    placeholder="Enter ZIP-code"
                    name="billing_zipcode"
                    type="text"
                    required="required"
                    value={values.billing_zipcode}
                    onChange={handleUpdate}
                    error={(localErrors && localErrors.billing_zipcode) || (errors && errors.billing_zipcode)}
                  />
                </div>

                <div className="w-full mb-4 md:w-1/2 md:pl-4">
                  <LabeledInput
                      className="bg-[#F6F6F6]"
                    title="City*"
                    placeholder="Enter city"
                    name="billing_city"
                    type="text"
                    required="required"
                    value={values.billing_city}
                    onChange={handleUpdate}
                    error={(localErrors && localErrors.billing_city) || (errors && errors.billing_city)}
                  />
                </div>
              </>
            }

            <div className="w-full mb-4">
              <LabeledSelect
                  className="bg-[#F6F6F6]"
                title="Currency"
                name="billing_currency"
                noDefault={true}
                options={[{value: 'sek', label: 'SEK'}]}
                onChange={handleUpdate}
                error={(localErrors && localErrors.billing_currency) || (errors && errors.billing_currency)}
              />
            </div>

          </div>

          <div className={`flex flex-wrap ${step === 2 ? '' : 'hidden'}`} ref={step_refrences[2]}>
            <div className="w-full mb-4">
              <LabeledInput
                  className="bg-[#F6F6F6]"
                title="Email*"
                placeholder="Enter e-mail"
                name="user_email"
                type="email"
                required="required"
                onChange={handleUpdate}
                value={values.user_email}
                error={(localErrors && localErrors.user_email) || (errors && errors.user_email)}
              />
            </div>

            <div className="w-full mb-4">
              <LabeledInput
                  className="bg-[#F6F6F6]"
                title="Choose password*"
                placeholder="Enter password"
                name="password"
                type="password"
                required="required"
                onChange={handleUpdate}
                value={values.password}
                error={(localErrors && localErrors.password) || (errors && errors.password)}
              />
            </div>

            <div className="w-full mb-4">
              <LabeledInput
                  className="bg-[#F6F6F6]"
                title="Confirm password*"
                placeholder="Confirm password"
                name="password_confirmation"
                type="password"
                required="required"
                onChange={handleUpdate}
                value={values.password_confirmation}
                error={(localErrors && localErrors.password_confirmation) || (errors && errors.password_confirmation)}
              />
            </div>
          </div>


        </form>

        <div className="flex flex-col md:flex-row items-center justify-between mt-4">
          <p className="mb-4">By clicking "Create account", I agree to Emmas <a className="underline hover:no-underline" target="_blank" href="/terms-of-service">Terms and conditions</a></p>
          <button className="ml-3 btn self-end" onClick={handleNextStep}>{step !== 2 ? 'Next' : 'Create account'}</button>
        </div>
      </div>

    </div>
  </Modal>
}
